// SDM — IN INFINITUM · fiche produit (section `sdm-fiche`) et son calque sur la page d'accueil.
//
// Porté de `demos/ux/_core/shop.js`. Deux rôles :
//   1. faire vivre une fiche : choix des options → variante, prix, repère « 2 / 4 », ajout au VRAI panier ;
//   2. sur la page d'accueil, ouvrir la fiche par-dessus au lieu de quitter la page. La fiche est extraite de
//      la page produit (`/products/<handle>`, avec les blocs d'app du gabarit) : c'est la même que celle de
//      la page produit, rendue par le même Liquid. L'URL du produit entre dans l'historique ; « Fermer » et le
//      retour du navigateur ramènent à l'accueil, au même endroit du labyrinthe. Un rechargement sur cette URL
//      donne la page produit, où « Fermer » est un simple lien vers l'accueil.
//
// Le labyrinthe passe par `window.sdmFiche.open(url)` et écoute `sdm:fiche-close` pour reculer sa caméra.
// Depuis la collection, la pièce cliquée vole jusqu'à sa place dans la fiche (assets/sdm-flight.js).

import { createFlight } from 'sdm/flight';

const root = document.documentElement;
const PRODUCT_PATH = /\/products\/[^/?#]+\/?$/;

/* ---------- habillage des pastilles Rubik ---------- */

// Rubik (CD / vinyle d'un pack, 0.4.43) dessine dans un shadow DOM que le CSS du thème n'atteint pas, et ses
// variables `--rcl-*` ne couvrent ni les capitales ni l'espacement des lettres. On y ajoute donc une feuille qui
// le calque sur le sélecteur de la fiche (demande JB, 0.4.44) : intitulé comme « Taille », cases égales et collées
// sur toute la largeur, même hauteur, choix en cours en nuit. Les couleurs viennent des variables de la fiche,
// qui traversent le shadow DOM. Rubik remet ses feuilles à zéro à chaque rendu (`rcl:ready`) : on repasse après.
const RUBIK_CSS = `
  .rcl.rcl { margin: 0 !important; padding: 0 !important; }
  .rcl .rcl__group { margin: 0 !important; }
  .rcl .rcl__group-label {
    margin: 0 0 10px !important;
    color: var(--sdm-fiche-muted, #6d7c84) !important;
    font-size: 10px !important;
    font-weight: 400 !important;
    letter-spacing: 0.24em;
    text-transform: uppercase;
  }
  .rcl .rcl__group-label-option-name { color: inherit !important; font: inherit !important; letter-spacing: inherit !important; }
  .rcl .rcl__group-label-separator,
  .rcl .rcl__group-label-selected-value { display: none !important; }
  .rcl .rcl__grid {
    display: grid !important;
    padding: 1px !important;
    gap: 1px !important;
    background: rgb(43 58 66 / 0.25);
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
  }
  .rcl .rcl__swatch--button {
    display: flex !important;
    box-sizing: border-box;
    width: auto !important;
    min-width: 0 !important;
    height: 46px !important;
    align-items: center;
    justify-content: center;
    padding: 0 12px !important;   /* 8 → 12 px de chaque côté (demande JB, 0.4.133) */
    border: 0 !important;
    border-radius: 0 !important;
    background: var(--sdm-fiche-bg, #eef3f6) !important;
    color: var(--sdm-fiche-fg, #2b3a42) !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    letter-spacing: 0.16em;
    text-decoration: none;
    text-transform: uppercase;
    transition: background-color 0.3s, color 0.3s;
  }
  .rcl .rcl__swatch--button:hover { background: #e3e9ed !important; }
  .rcl .rcl__swatch--button.rcl__swatch--active {
    background: var(--sdm-fiche-fg, #2b3a42) !important;
    color: var(--sdm-fiche-light, #e6ecef) !important;
  }
  .rcl .rcl__swatch--button:focus-visible { outline: 1px solid var(--sdm-fiche-fg, #2b3a42); outline-offset: 2px; }
  .rcl .rcl__button-label { color: inherit !important; font: inherit !important; letter-spacing: inherit; }
`;
let rubikSheet = null;

function habillerRubik(rcl) {
  const root = rcl.shadowRoot;
  if (!root) return;
  if (!rubikSheet) {
    try { rubikSheet = new CSSStyleSheet(); rubikSheet.replaceSync(RUBIK_CSS); } catch { return; }
  }
  const sheets = root.adoptedStyleSheets.filter((s) => s !== rubikSheet);
  root.adoptedStyleSheets = [...sheets, rubikSheet];
}

function suivreRubik(fiche) {
  const zone = fiche.querySelector('.sdm-fiche-app');
  if (!zone) return;
  const vus = new WeakSet();
  const prendre = () => zone.querySelectorAll('rubik-combined-listings').forEach((rcl) => {
    habillerRubik(rcl);
    if (vus.has(rcl)) return;
    vus.add(rcl);
    // après son rendu, et après la feuille « CSS personnalisé » que l'app ajoute dans la foulée
    rcl.addEventListener('rcl:ready', () => { habillerRubik(rcl); setTimeout(() => habillerRubik(rcl)); });
  });
  prendre();
  new MutationObserver(prendre).observe(zone, { childList: true, subtree: true });
}

/* ---------- une fiche ---------- */

function initFiche(el) {
  if (el.dataset.ready) return;
  el.dataset.ready = '1';
  suivreRubik(el);

  const form = el.querySelector('[data-sdm-fiche-form]');
  const idInput = el.querySelector('[data-variant-id]');
  const addBtn = el.querySelector('[data-add]');
  const priceEl = el.querySelector('[data-price]');
  const msgEl = el.querySelector('[data-msg]');
  const segs = [...el.querySelectorAll('[data-option]')];
  let variants = [];
  try { variants = JSON.parse(el.querySelector('[data-sdm-variants]')?.textContent || '[]'); } catch { /* fiche sans variantes lisibles : le formulaire garde la première */ }

  const selected = () => segs.map((s) => s.querySelector('[aria-pressed="true"]')?.dataset.value ?? null);
  const find = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i])) ?? null;

  function sync() {
    const opts = selected();
    const v = find(opts);
    if (v) {
      idInput.value = v.id;
      if (priceEl) priceEl.textContent = v.price;
    }
    const ok = !!v?.available;
    addBtn.disabled = !ok;
    addBtn.textContent = !v ? 'Indisponible' : ok ? (addBtn.dataset.libelle || 'Ajouter au panier') : 'Épuisé';   // « Précommander » avant la sortie
    // barre chaque valeur qui, avec les autres choix en cours, mène à une variante épuisée ou absente
    segs.forEach((seg, i) => {
      seg.querySelectorAll('button').forEach((b) => {
        const alt = opts.slice();
        alt[i] = b.dataset.value;
        b.toggleAttribute('data-epuise', !find(alt)?.available);
      });
    });
    if (msgEl) msgEl.textContent = '';
  }

  el.addEventListener('click', (e) => {
    const b = e.target.closest('[data-option] button');
    if (!b) return;
    b.closest('[data-option]').querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    sync();
  });

  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    addToCart(form, addBtn, msgEl);
  });

  if (segs.length) sync();
  watchShots(el);
  watchVideos(el);
  initTiroir(el);
  return el;
}

// « Détails du produit » / « Livraisons et retours » : le tiroir de droite (un <dialog> modal, donc au-dessus de
// l'en-tête et du calque, focus piégé et Échap natifs). Un seul tiroir, deux contenus. Il se referme par le
// bouton, Échap ou un clic sur le voile ; la sortie est animée avant la fermeture réelle.
function initTiroir(el) {
  const dlg = el.querySelector('[data-sdm-tiroir-dialog]');
  if (!dlg) return;
  const titre = dlg.querySelector('[data-sdm-tiroir-titre]');
  const panneaux = [...dlg.querySelectorAll('[data-sdm-panneau]')];
  let fermeture = null;

  function ouvrir(quoi, lien) {
    clearTimeout(fermeture);
    dlg.classList.remove('sdm-tiroir--sortie');
    panneaux.forEach((p) => { p.hidden = p.dataset.sdmPanneau !== quoi; });
    const actif = panneaux.find((p) => !p.hidden);
    if (titre) titre.textContent = actif?.dataset.titre ?? '';
    dlg.setAttribute('aria-label', actif?.dataset.titre ?? 'Informations produit');
    dlg._retour = lien;
    if (!dlg.open) dlg.showModal();
    actif?.scrollTo?.(0, 0);
  }

  function fermer() {
    if (!dlg.open || dlg.classList.contains('sdm-tiroir--sortie')) return;
    const reduit = matchMedia('(prefers-reduced-motion: reduce)').matches;
    dlg.classList.add('sdm-tiroir--sortie');
    fermeture = setTimeout(() => {
      dlg.classList.remove('sdm-tiroir--sortie');
      dlg.close();
      dlg._retour?.focus?.({ preventScroll: true });
    }, reduit ? 0 : 280);
  }

  el.addEventListener('click', (e) => {
    const lien = e.target.closest('[data-sdm-tiroir]');
    if (lien && el.contains(lien) && !dlg.contains(lien)) { e.preventDefault(); ouvrir(lien.dataset.sdmTiroir, lien); }
  });
  dlg.addEventListener('click', (e) => {
    // le voile : un clic sur le <dialog> lui-même, hors de son contenu
    if (e.target === dlg || e.target.closest('[data-sdm-tiroir-fermer]')) fermer();
  });
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); fermer(); });   // Échap : même sortie animée
}

// Le repère « 2 / 4 » suit la vue la plus visible. Rien à faire pour une pièce à une seule vue.
function watchShots(el) {
  const repere = el.querySelector('[data-count]');
  if (!repere) return;
  // les vues seules : ni le chiffre du pack (fond fixe) ni les visuels de composition posés dessus
  // (la photo d'un pack est dans un cadre avec son phono en coin : sa première image, 0.4.109) ; une vidéo compte
  // pour une vue, par sa boîte (demande JB, 0.4.128)
  const vues = [...el.querySelectorAll('.sdm-fiche-shot > img, .sdm-fiche-shot > svg, .sdm-fiche-cadre > img:first-child, .sdm-fiche-shot > .sdm-fiche-video')];
  // Chevrons du carrousel mobile : une vue de plus ou de moins ; celui du bout s'efface (disabled)
  const media = el.querySelector('.sdm-fiche-media');
  const [prec, suiv] = ['-1', '1'].map((d) => el.querySelector(`[data-sdm-vue="${d}"]`));
  for (const b of [prec, suiv]) {
    b?.addEventListener('click', () => media.scrollBy({ left: Number(b.dataset.sdmVue) * media.clientWidth, behavior: 'smooth' }));
  }
  const vu = new Map();
  const io = new IntersectionObserver((entries) => {
    for (const en of entries) vu.set(en.target, en.intersectionRatio);
    let tete = vues[0], part = -1;
    for (const img of vues) { const r = vu.get(img) ?? 0; if (r > part) { tete = img; part = r; } }
    const i = vues.indexOf(tete);
    repere.textContent = `${i + 1} / ${vues.length}`;
    if (prec) prec.disabled = i <= 0;
    if (suiv) suiv.disabled = i >= vues.length - 1;
  }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
  vues.forEach((img) => io.observe(img));
}

// Vidéos Shopify de la galerie (demande JB, 0.4.128) : muettes, en boucle, elles ne jouent que visibles (sur
// ordinateur les vues s'empilent, sur mobile une seule est à l'écran) — pas de lecture ni de téléchargement pour
// rien. Mouvement réduit : pas de lecture automatique, la vidéo reste sur son affiche avec ses contrôles.
function watchVideos(el) {
  const videos = [...el.querySelectorAll('.sdm-fiche-video video')];
  if (!videos.length) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    for (const v of videos) { v.autoplay = false; v.pause(); v.controls = true; }
    return;
  }
  const io = new IntersectionObserver((entries) => {
    for (const { target: v, isIntersecting } of entries) {
      if (isIntersecting) v.play()?.catch(() => { /* lecture refusée (économie d'énergie) : l'affiche reste */ });
      else v.pause();
    }
  }, { threshold: 0.25 });
  for (const v of videos) {
    v.muted = true;   // la fiche du calque arrive par innerHTML : l'attribut seul ne suffit pas à Safari pour l'autoplay
    io.observe(v);
  }
}

/* ---------- panier : celui du thème ----------
   Même protocole que le formulaire produit d'Horizon (assets/product-form.js) : un `CartLinesUpdateEvent`
   annoncé avant la requête, résolu avec le panier à jour. Le tiroir, la pastille de l'en-tête et les lignes du
   panier l'écoutent déjà : ils se mettent à jour d'eux-mêmes. */

const routes = () => window.Theme?.routes ?? {};
const cartAddUrl = () => routes().cart_add_url ?? `${window.Shopify?.routes?.root ?? '/'}cart/add.js`;
const cartUrl = () => routes().cart_url ?? `${window.Shopify?.routes?.root ?? '/'}cart`;

let events = null;
const loadEvents = () => (events ??= import('@shopify/events').catch(() => ({})));

async function addToCart(form, btn, msgEl) {
  if (btn.disabled) return;
  btn.disabled = true;
  const label = btn.textContent;
  btn.textContent = '…';

  const { CartLinesUpdateEvent } = await loadEvents();
  const body = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent?.createPromise?.();
  if (deferred) {
    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(body.get('id')), quantity: 1 }],
      promise: deferred.promise,
    }));
  }

  try {
    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Ajout impossible');

    const cart = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'sdm-fiche', itemCount: 1, sections: data.sections, didError: false },
    });
    openCart(msgEl);
  } catch (err) {
    deferred?.reject(Object.assign(new Error(err.message), { name: 'AbortError' }));   // sans bruit dans la console des composants
    if (msgEl) msgEl.textContent = err.message;
  } finally {
    btn.textContent = label;
    btn.disabled = false;
  }
}

// Le tiroir s'ouvre seul si le réglage « ouvrir le panier à l'ajout » est coché ; sinon on l'ouvre ici. Sans
// tiroir (panier en page), un mot sous le bouton, avec le lien.
function openCart(msgEl) {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer) { if (!drawer.isOpen) drawer.open?.(); return; }
  if (msgEl) msgEl.innerHTML = `Ajouté — <a href="${cartUrl()}">voir le panier</a>`;
}

/* ---------- le calque de la page d'accueil ---------- */

// La fiche entièrement montrée : vol (720 ms) + relais (220 ms) de sdm-flight.js, ou mise en place des vues (0,9 s).
const FICHE_PRETE_MS = 950;

function createLayer() {
  const layer = Object.assign(document.createElement('div'), { className: 'sdm-fiche-layer', hidden: true });
  layer.setAttribute('role', 'dialog');
  layer.setAttribute('aria-modal', 'true');
  layer.setAttribute('aria-label', 'Fiche produit');
  document.body.append(layer);

  const flight = createFlight();

  // La case d'où la pièce est partie est oubliée et récupère sa pièce : plus rien ne vole ensuite
  // (fermeture de la fiche, push vers le pack).
  function oublierDepart() {
    document.querySelectorAll('.sdm-flying').forEach((img) => img.classList.remove('sdm-flying'));
    flight.from(null);
  }

  const cache = new Map();   // url → promesse du HTML de la fiche
  let current = null, pushed = false, lastFocus = null, titreAvant = document.title, urlAvant = null, jeton = 0;

  const clean = (url) => { const u = new URL(url, location.href); u.search = ''; u.hash = ''; return u; };

  function load(url) {
    const u = clean(url);
    const key = u.pathname;
    if (!cache.has(key)) {
      // La page produit entière, pas la section seule (0.4.43) : rendue par son nom de fichier, la section n'a
      // pas les blocs d'app du gabarit (Rubik), et son id dans le gabarit (`template--<n°>__main`) change d'un
      // thème à l'autre. Écart mesuré : ~70 ms, absorbé par le préchargement au survol.
      cache.set(key, fetch(u, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error(`fiche ${r.status}`);
        return r.text();
      }).catch((err) => { cache.delete(key); throw err; }));
    }
    return cache.get(key);
  }

  // Rubik Combined Listings (CD / vinyle d'un même pack) : sur la page produit, l'app construit ses pastilles
  // elle-même, mais pas sur l'accueil (elle ne s'occupe que du gabarit « product »). Dans le calque, on les
  // construit comme elle : sa fonction publique `create`, les données JSON que porte son bloc, ses réglages
  // de fiche produit (ordinateur ou mobile, au même seuil qu'elle).
  function initRubik(fiche) {
    const rcl = window.RubikCombinedListings;
    if (typeof rcl?.create !== 'function') return;
    const mobile = innerWidth < (rcl._CONFIG?.mobileBreakpoint ?? 750);
    fiche.querySelectorAll('.sdm-fiche-app [data-rcl-product-id]').forEach((place) => {
      if (place.querySelector('rubik-combined-listings')) return;
      const json = place.closest('.sdm-fiche-app').querySelector('script[data-rubik-swatch-product-id]');
      if (!json) return;
      try {
        const data = JSON.parse(json.textContent);
        const brut = data.groups?.[0]?.settings?.visual_settings?.[mobile ? 'product_page_mobile' : 'product_page_desktop'];
        // les clés que l'app renomme avant `create` (sa fonction interne Z) : boutons avec image, style actif…
        const reglages = brut && {
          ...brut,
          buttonActiveStyle: brut.buttonStyles?.activeStyle || 'border',
          buttonShowImage: !!brut.buttonStyles?.showImage,
          buttonShowPrice: !!brut.buttonStyles?.showPrice,
          showOutOfStockLine: brut.styles?.showOutOfStockLine ?? true,
          buttonShowOutOfStockLine: brut.buttonStyles?.showOutOfStockLine ?? true,
        };
        const wrap = document.createElement('div');
        wrap.className = 'rcl-swatch-wrapper rcl-product-page';
        wrap.append(rcl.create(data, reglages));
        place.style.display = '';
        place.append(wrap);
      } catch (err) {
        console.warn('[sdm] pastilles Rubik indisponibles dans la fiche', err);
      }
    });
  }

  // Le fond hors d'atteinte tant que la fiche est ouverte : clavier et lecteurs d'écran ne le traversent plus.
  // Restent libres le tiroir du panier, qui s'ouvre par-dessus, et l'en-tête SDM, qui reste visible au-dessus de
  // la fiche (compte, panier) : il vit dans `.page-wrapper`, dont on ne rend donc inerte que le reste.
  function setInert(on) {
    for (const el of document.body.children) {
      if (el === layer || el.matches('script, theme-drawer, cart-drawer-component') || el.querySelector('cart-drawer-component')) continue;
      if (el.classList.contains('page-wrapper')) {
        for (const enfant of el.children) if (enfant.id !== 'header-group') enfant.toggleAttribute('inert', on);
        continue;
      }
      el.toggleAttribute('inert', on);
    }
  }

  async function open(url, { push = true } = {}) {
    const path = clean(url).pathname;
    if (!PRODUCT_PATH.test(path)) return false;
    const n = ++jeton;
    if (!current) { lastFocus = document.activeElement; titreAvant = document.title; urlAvant = location.href; }
    current = path;

    layer.hidden = false;
    layer.setAttribute('aria-busy', 'true');
    root.classList.add('sdm-fiche-open');
    setInert(true);
    document.dispatchEvent(new CustomEvent('sdm:fiche-open'));
    requestAnimationFrame(() => requestAnimationFrame(() => { if (current === path) layer.classList.add('on'); }));
    if (push) {
      if (history.state?.sdmFiche) history.replaceState({ sdmFiche: path }, '', path);
      else { history.pushState({ sdmFiche: path }, '', path); pushed = true; }
    }

    let html;
    try { html = await load(path); } catch (err) {
      console.warn('[sdm] fiche indisponible, page produit à la place', err);
      location.assign(path);
      return true;
    }
    if (n !== jeton) return true;   // un autre produit a été demandé entre-temps

    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const fiche = tpl.content.querySelector('[data-sdm-fiche]');
    if (!fiche) { location.assign(path); return true; }
    layer.replaceChildren(fiche);
    layer.removeAttribute('aria-busy');
    layer.scrollTop = 0;
    initFiche(fiche);
    initRubik(fiche);
    // Cabine miroirs (snippets/cabine-miroirs.liquid) : un script du HTML injecté ne s'exécute pas, on charge
    // donc le module ici quand la fiche en porte une.
    const cabine = fiche.querySelector('mirror-cabin') && fiche.querySelector('script[type="module"][src*="cabine-miroirs"]');
    if (cabine) import(cabine.src).catch((err) => console.warn('[sdm] cabine miroirs indisponible', err));
    // sans case de départ (labyrinthe, historique), rien ne vole : le fondu suffit. La fiche montrée en entier
    // (vol + relais, ou mise en place des vues), elle est « prête » : la cabine peut alors apparaître (demande JB).
    layer.classList.remove('sdm-fiche-prete');
    flight.open(layer).then(() => {
      setTimeout(() => { if (n === jeton) layer.classList.add('sdm-fiche-prete'); }, FICHE_PRETE_MS);
    });
    document.title = fiche.dataset.title || titreAvant;
    fiche.querySelector('[data-sdm-fiche-close]')?.focus({ preventScroll: true });
    return true;
  }

  function hide() {
    if (!current) return;
    current = null;
    jeton++;
    // Retour : sur mobile, la pièce revole vers sa case (demande JB, 0.4.88) ; sur ordinateur, pas de vol retour
    // (0.4.77) : la case récupère sa pièce et la fiche s'efface en fondu. Même seuil que la fiche mobile.
    if (!matchMedia('(max-width: 720px)').matches) oublierDepart();
    flight.close(layer);   // avant le fondu : la pièce est mesurée à sa place dans la fiche (sans case : rien ne vole)
    layer.classList.remove('on', 'sdm-fiche-prete');
    root.classList.remove('sdm-fiche-open');
    setInert(false);
    document.title = titreAvant;
    setTimeout(() => { if (!current) { layer.hidden = true; layer.replaceChildren(); } }, 650);
    lastFocus?.focus?.({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('sdm:fiche-close'));
  }

  // Ouverte ici : on revient dans l'historique, le popstate masque la fiche. Sinon (arrivée directe sur une URL
  // de l'accueil portant l'état, cas rare) : on remet l'URL de l'accueil sans recharger.
  function close() {
    if (!current) return;
    if (pushed) { pushed = false; history.back(); return; }
    // la page d'où la fiche a été ouverte (accueil ou collection) ; l'accueil si l'on est arrivé sur la fiche
    const avant = urlAvant && !PRODUCT_PATH.test(new URL(urlAvant).pathname) ? urlAvant : null;
    history.replaceState(null, '', avant ?? window.Shopify?.routes?.root ?? '/');
    hide();
  }

  addEventListener('popstate', (e) => {
    const path = e.state?.sdmFiche;
    if (path) { if (path !== current) open(path, { push: false }); }
    else hide();
  });

  layer.addEventListener('click', (e) => {
    if (e.target.closest('[data-sdm-fiche-close]')) { e.preventDefault(); close(); return; }
    // une pastille Rubik (dans son shadow DOM) : l'autre version du pack s'ouvre dans le calque, sans quitter
    // l'accueil ; de même le push d'une pièce seule vers son pack (SDM.tasks.039)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.composedPath().find((n) => n instanceof HTMLAnchorElement);
    if (!a || !(e.target.closest?.('rubik-combined-listings') || a.matches('[data-sdm-push-pack]'))) return;
    const u = new URL(a.href, location.href);
    if (u.origin !== location.origin || !PRODUCT_PATH.test(u.pathname)) return;
    e.preventDefault();
    // Pas de vol quand on change de produit depuis la fiche — push vers le pack ou pastilles CD / vinyle (demandes
    // JB, 0.4.89) : la case d'où partait la première pièce est oubliée et récupère sa pièce, l'autre produit
    // s'ouvre en fondu et se refermera sans retour en vol
    oublierDepart();
    if (u.pathname !== current) open(u.pathname);
  });
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current && !document.querySelector('dialog[open]')) close();
  });

  // Les liens produit de la page d'accueil (les îlots de la collection) ouvrent la fiche ; un clic modifié
  // (nouvel onglet, etc.) reste au navigateur.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a[href]');
    if (!a || a.target === '_blank' || layer.contains(a) || !a.closest('.sdm-collection')) return;
    const u = new URL(a.href, location.href);
    if (u.origin !== location.origin || !PRODUCT_PATH.test(u.pathname)) return;
    e.preventDefault();
    // Carte à vidéo (premier média, 0.4.130) : pas de vol, la fiche s'ouvre en fondu — l'image du coin ou des
    // visuels de lot ne doit pas décoller à la place de la pièce.
    flight.from(a.querySelector('.sdm-tile-video') ? null : a.querySelector('.sdm-tile img'));
    open(u.pathname);
  });

  // Préchargement au survol : la fiche est souvent prête avant le clic — son HTML, et sa première image, que
  // le vol de la pièce (sdm-flight.js) doit pouvoir mesurer dès l'ouverture. Mêmes srcset/sizes que la fiche :
  // le navigateur choisit le même fichier, servi ensuite depuis son cache.
  const imagesPrechargees = new Set();
  function preloadImage(path, html) {
    if (imagesPrechargees.has(path)) return;
    imagesPrechargees.add(path);
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const img = tpl.content.querySelector('.sdm-fiche-shot img');
    if (!img) return;
    const pre = new Image();
    pre.sizes = img.sizes;
    pre.srcset = img.srcset;
    pre.src = img.src;
  }

  document.addEventListener('pointerover', (e) => {
    const a = e.target.closest?.('.sdm-collection a[href]');
    if (!a) return;
    const u = new URL(a.href, location.href);
    if (u.origin === location.origin && PRODUCT_PATH.test(u.pathname)) {
      load(u.pathname).then((html) => preloadImage(u.pathname, html), () => {});
    }
  }, { passive: true });

  return { open, close, prefetch: (url) => load(url).catch(() => {}), get isOpen() { return !!current; } };
}

/* ---------- démarrage ---------- */

/* ---------- vidéos des cartes (0.4.134) ----------
   snippets/sdm-carte-video.liquid : `preload="none"`, sans autoplay — rien n'est téléchargé pendant le preloader, la
   3D du labyrinthe garde la bande passante pour elle. Après lui, les vidéos se préchargent ; chacune est lancée quand
   sa carte est à l'écran et mise en pause dès qu'elle en sort. Mouvement réduit : l'affiche reste. */
function cartesVideos() {
  const videos = [...document.querySelectorAll('video[data-sdm-carte-video]')];
  if (!videos.length) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // mouvement réduit : pas de lecture, mais la première image (plus d'affiche, 0.4.137)
    videos.forEach((v) => { v.preload = 'metadata'; v.load(); });
    return;
  }
  const io = new IntersectionObserver((entries) => {
    for (const { target: v, isIntersecting } of entries) {
      if (isIntersecting) { v.muted = true; v.play()?.catch(() => { /* lecture refusée : l'affiche reste */ }); }
      else v.pause();
    }
  }, { threshold: 0.25 });
  // Une fois le preloader parti (et son fondu fini, 0,9 s) : préchargement en tâche de fond, pour que la vidéo soit
  // prête quand on descend jusqu'aux cartes (demande JB, 0.4.135) ; la lecture, elle, attend toujours l'écran.
  // En entier sur ordinateur seulement (souris) : au téléphone, 11 Mo pour des cartes qu'on ne verra peut-être pas —
  // chaque vidéo s'y charge à l'arrivée de sa carte (choix JB).
  const precharger = matchMedia('(hover: hover) and (pointer: fine)').matches && !navigator.connection?.saveData;
  const demarrer = () => {
    videos.forEach((v) => io.observe(v));
    // au téléphone, seulement le début de chaque vidéo (métadonnées) : de quoi dessiner sa vraie première image, sans
    // affiche (0.4.137)
    setTimeout(() => videos.forEach((v) => {
      const mode = precharger ? 'auto' : 'metadata';
      if (v.preload === mode) return;
      v.preload = mode;
      if (v.readyState === 0 && v.paused) v.load();
    }), 900);
  };
  if (document.getElementById('sdm-preloader') && !window.SDM_PRELOADER_FINI) {
    document.addEventListener('sdm:preloader-fin', demarrer, { once: true });
  } else demarrer();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cartesVideos, { once: true });
else cartesVideos();

/* ---------- précommande : le jour de la sortie (SDM.tasks.025) ----------
   Porté de ProductReleaseGuard (thème de test d'Arthur). Liquid juge la précommande à l'heure du rendu ; une page
   restée en cache le ferait encore après la sortie. <sdm-sortie data-sortie="2026-10-01T00:00:00+02:00"> bascule
   donc à l'heure dite : [data-avant] s'efface, [data-apres] paraît, l'élément disparaît s'il porte data-cacher,
   et le bouton de sa fiche redevient « Ajouter au panier ». Élément personnalisé : il s'active aussi dans la fiche
   en calque, chargée après coup. */
if (!customElements.get('sdm-sortie')) {
  customElements.define('sdm-sortie', class extends HTMLElement {
    #minuteur = 0;
    connectedCallback() {
      const sortie = Date.parse(this.dataset.sortie ?? '');
      if (Number.isNaN(sortie)) return;
      const delai = sortie - Date.now();
      if (delai <= 0) this.#sortir();
      else if (delai < 2 ** 31 - 1) this.#minuteur = setTimeout(() => this.#sortir(), delai);
    }
    disconnectedCallback() { clearTimeout(this.#minuteur); }
    #sortir() {
      this.querySelectorAll('[data-avant]').forEach((e) => { e.hidden = true; });
      this.querySelectorAll('[data-apres]').forEach((e) => { e.hidden = false; });
      if (this.hasAttribute('data-cacher')) this.hidden = true;
      const btn = this.closest('[data-sdm-fiche]')?.querySelector('[data-add][data-libelle]');
      if (btn) {
        delete btn.dataset.libelle;
        if (!btn.disabled) btn.textContent = 'Ajouter au panier';
      }
    }
  });
}

document.querySelectorAll('[data-sdm-fiche]').forEach(initFiche);

// Le calque n'a de sens que là où des pièces s'affichent en `.sdm-collection` (l'accueil, les pages collection),
// et jamais dans l'éditeur de thème, où l'on doit pouvoir naviguer normalement.
if (!window.Shopify?.designMode && document.querySelector('.sdm-collection, [data-sdm-lab]')) {
  window.sdmFiche = createLayer();
}
